import React, { useEffect, useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import { DownloadModel } from "./DownloadModel";
import Utils from "../../core/Utils/Utils";
import VersionPopup from "../VersionPopup/VersionPopup";
import ReleaseNotesPopup from "../ReleaseNotesPopup/ReleaseNotesPopup";
import { Theme } from "../../core/Models/CustomSiteConfigModel";
import { themeService } from "../../core/NotificationStripNav/ThemeService";
import { ReactComponent as InstallerSvg } from "../../../assets/images/download-svgrepo-com.svg";
import { ReactComponent as ReleaseNotesSvg } from "../../../assets/images/checklist-svgrepo-com.svg";
import { ReactComponent as UserGuideSvg } from "../../../assets/images/user-guide-svgrepo-com.svg";
import { useNotification } from "../../core/Notification/NotificationProvider";

import "./Download.css";

const Download: React.FC<{ blok: DownloadModel; rowIndex: number }> = ({
  blok,
  rowIndex,
}) => {
  const { addNotification } = useNotification();
  const [theme, setTheme] = useState<Theme | null>(themeService.getTheme());
  let [versionId, setVersionId] = useState("");
  let [releaseNotesId, setReleaseNotesId] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const versionId = params.get("versionId");
    const releaseNotesId = params.get("releaseNotesId");
    setVersionId(versionId || "");
    setReleaseNotesId(releaseNotesId || "");

    const unsubscribe = themeService.subscribe(setTheme);
    return () => unsubscribe();
  }, []);

  const setVersionIdValue = (value: string) => {
    Utils.setNavidation([
      { param: "versionId", value },
      { param: "releaseNotesId", value: "" },
    ]);

    setVersionId(value);
    setReleaseNotesId("");
  };
  const setReleaseNotesIdValue = (value: string) => {
    Utils.setNavidation([
      { param: "versionId", value: "" },
      { param: "releaseNotesId", value },
    ]);

    setVersionId("");
    setReleaseNotesId(value);
  };
  const handleVersionClick = () => {
    setVersionIdValue(blok._uid || "");
  };
  const handleReleaseClick = () => {
    setReleaseNotesIdValue(blok._uid || "");
  };
  const handleInstallerClick = () => {
    const url = new URL(blok.installerUrl.cached_url, window.location.origin);
    window.location.href = url.href;
  };
  const handleContextMenuInstallerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const url = new URL(blok.installerUrl.cached_url, window.location.origin);
    navigator.clipboard.writeText(url.href);
    addNotification("Link copied to clipboard!", "notification");
  };
  const handleUninstallerClick = () => {
    const url = new URL(blok.uninstallerUrl.cached_url, window.location.origin);
    window.location.href = url.href;
  };
  const handleUserGuideClick = () => {
    const url = new URL(blok.userGuideUrl.cached_url, window.location.origin);
    window.open(url.href, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      {...storyblokEditable(blok)}
      className="download-comp row"
      style={{
        backgroundColor:
          rowIndex % 2 === 0
            ? theme?.tableEvenColumnsColor
            : theme?.tableOddColumnsColor,
      }}
    >
      <div className="cell col-md-3 text-left" title={blok.downloadName}>
        {blok.downloadName}
      </div>
      <div className="cell release-date text-left">
        {Utils.getDownloadFormattedDate(blok.releaseDate)}
      </div>
      <div className="cell">
        <span className="underline-button" onClick={handleVersionClick}>
          {blok.versionId}
        </span>
      </div>
      <div className="cell">
        <span className="" title={blok.minimalOs}>
          {blok.minimalOs}
        </span>
      </div>
      <div className="cell img-cell">
        {blok.releaseNotes && (
          <ReleaseNotesSvg
            fill={theme?.textColor}
            className="release-notes-img"
            onClick={handleReleaseClick}
          ></ReleaseNotesSvg>
        )}
      </div>
      <div
        className="cell img-cell"
        title="Copy link to clipboard by right clicking the icon"
      >
        {blok.installerUrl && blok.installerUrl.cached_url && (
          <InstallerSvg
            fill={theme?.textColor}
            className="release-notes-img"
            onClick={handleInstallerClick}
            onContextMenu={handleContextMenuInstallerClick}
          ></InstallerSvg>
        )}
      </div>
      <div className="cell img-cell">
        {blok.uninstallerUrl && blok.uninstallerUrl.cached_url && (
          <InstallerSvg
            fill={theme?.textColor}
            onClick={handleUninstallerClick}
            className="release-notes-img"
          ></InstallerSvg>
        )}
      </div>
      <div className="cell img-cell">
        {blok.userGuideUrl && blok.userGuideUrl.cached_url && (
          <UserGuideSvg
            fill={theme?.textColor}
            className="user-guide-img"
            onClick={handleUserGuideClick}
          ></UserGuideSvg>
        )}
      </div>
      {blok.isBeta && <div className="download-is-beta">BETA</div>}
      {blok.recentlyUpdated && (
        <div className="download-is-recently-updated">RECENTLY UPDATED</div>
      )}

      {versionId === blok._uid && (
        <VersionPopup
          blok={blok}
          onButtonClick={() => setVersionIdValue("")}
          key={`VersionPopup-${blok._uid}`}
        ></VersionPopup>
      )}

      {releaseNotesId === blok._uid && (
        <ReleaseNotesPopup
          blok={blok}
          onButtonClick={() => setReleaseNotesIdValue("")}
          key={`ReleaseNotesPopup-${blok._uid}`}
        ></ReleaseNotesPopup>
      )}
    </div>
  );
};

export default Download;
